import { Roles } from "./roles";
import {
  getUserByEmail,
  getUsers,
  getUserById,
  hasUpstreamListPayload,
  notifyAdminErrorMessage,
  submittingUserSubstitutedMessage,
} from "./api";

const submittingUserSubstitutionNotifyKeys = new Set();

export const GOODLEAP_SUBMITTING_USER_DOMAIN = "goodleap.com";

export const submittingUserSubstituteNotFoundMessage =
  "No submitting user found and no eligible substitute user found for configured roles";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function normalizeSubmittingUserEmail(email) {
  let normalized = String(email ?? "").trim();
  if (!normalized) return "";
  try {
    if (normalized.includes("%")) {
      normalized = decodeURIComponent(normalized);
    }
  } catch (err) {
    console.log("Could not decode submitting user email", err);
  }
  return normalized.trim();
}

export function submittingUserCheckKey(sharedAppId, tokenPart, email) {
  return `${sharedAppId}|${tokenPart}|${normalizeSubmittingUserEmail(email).toLowerCase()}`;
}

export function isGoodleapDomainEmail(email) {
  const normalized = normalizeSubmittingUserEmail(email).toLowerCase();
  if (!normalized.includes("@")) return false;
  return normalized.endsWith(`@${GOODLEAP_SUBMITTING_USER_DOMAIN}`);
}

export function emailsMatch(left, right) {
  return (
    normalizeSubmittingUserEmail(left).toLowerCase() ===
    normalizeSubmittingUserEmail(right).toLowerCase()
  );
}

export function userEmailFromRecord(user, userById) {
  return normalizeSubmittingUserEmail(userById?.email ?? user?.email ?? "");
}

export function roleIsEligible(roleName) {
  const normalized = String(roleName ?? "").trim().toLowerCase();
  if (!normalized) return false;
  return Roles.some((role) => role.toLowerCase() === normalized);
}

export function roleNameFromUserRecords(user, userById) {
  const roleName = userById?.role?.name ?? user?.role?.name ?? "";
  return String(roleName).trim();
}

function isResolvedUserRecord(userById) {
  return (
    userById != null &&
    userById !== false &&
    typeof userById === "object" &&
    !Array.isArray(userById)
  );
}

export function salesRepFromUserRecord(user, userById) {
  return {
    firstName: userById?.firstName ?? user?.firstName ?? "",
    lastName: userById?.lastName ?? user?.lastName ?? "",
    email: userEmailFromRecord(user, userById),
    phone:
      userById?.phoneNumber?.number ??
      user?.phoneNumber?.number ??
      user?.phones?.[0]?.number ??
      "",
  };
}

async function fetchWithRetry(fetchFn, attempts = 4, delayMs = 2000) {
  let lastResponse = null;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    lastResponse = await fetchFn();
    if (hasUpstreamListPayload(lastResponse)) {
      return lastResponse;
    }
    if (isResolvedUserRecord(lastResponse)) {
      return lastResponse;
    }
    if (attempt < attempts - 1) {
      await sleep(delayMs * (attempt + 1));
    }
  }
  return lastResponse;
}

/** True when the configured (DB) submitting user exists in partner API. */
export async function configuredSubmittingUserExists(
  clientId,
  submittingUserEmail,
  token
) {
  const result = await checkAndResolveSubmittingUser(
    clientId,
    submittingUserEmail,
    token
  );
  return result.status === "configured";
}

function findConfiguredUserInList(users, submittingUserEmail) {
  return users.find((user) =>
    emailsMatch(userEmailFromRecord(user, null), submittingUserEmail)
  );
}

function pickSubstituteCandidate(candidates) {
  for (const role of Roles) {
    const match = candidates.find(
      (candidate) =>
        candidate.roleName.toLowerCase() === role.toLowerCase()
    );
    if (match) {
      return {
        substitute: salesRepFromUserRecord(match.user, match.userById),
        roleName: match.roleName,
      };
    }
  }
  return null;
}

/**
 * Substitute when configured user was not found: first match by Roles priority,
 * excluding @goodleap.com emails.
 * @returns {{ substitute: object, roleName: string }|null}
 */
export async function findSubstituteSubmittingUser(clientId, token) {
  const usersResponse = await fetchWithRetry(() => getUsers(clientId, token));
  if (!hasUpstreamListPayload(usersResponse)) return null;

  const result = await findSubstituteSubmittingUserFromList(
    usersResponse.data,
    clientId,
    token
  );
  if (result?.inconclusive) return null;
  return result;
}

async function findSubstituteSubmittingUserFromList(users, clientId, token) {
  if (!users?.length) {
    return { inconclusive: true, reason: "partner user list empty" };
  }

  const candidates = [];
  let detailLookupAttempted = 0;
  let detailLookupSucceeded = 0;

  for (const user of users) {
    let userById = null;
    if (user?.id) {
      detailLookupAttempted += 1;
      userById = await getUserById(clientId, user.id, token);
      if (!isResolvedUserRecord(userById)) {
        userById = null;
      } else {
        detailLookupSucceeded += 1;
      }
    }

    const email = userEmailFromRecord(user, userById);
    if (isGoodleapDomainEmail(email)) continue;

    const roleName = roleNameFromUserRecords(user, userById);
    if (!roleIsEligible(roleName)) continue;

    candidates.push({ user, userById, roleName });
  }

  const substitute = pickSubstituteCandidate(candidates);
  if (substitute) return substitute;

  if (
    detailLookupAttempted > 0 &&
    detailLookupSucceeded === 0 &&
    candidates.length === 0
  ) {
    return { inconclusive: true, reason: "user detail lookup failed" };
  }

  return null;
}

/**
 * Resolve configured vs substitute submitting user.
 * Returns inconclusive when partner API data could not be verified (no Slack).
 */
export async function checkAndResolveSubmittingUser(
  clientId,
  submittingUserEmail,
  token
) {
  const email = normalizeSubmittingUserEmail(submittingUserEmail);
  if (!clientId || !email) {
    return { status: "inconclusive", reason: "missing client or email" };
  }

  const byEmail = await fetchWithRetry(() =>
    getUserByEmail(clientId, email, token)
  );
  if (!hasUpstreamListPayload(byEmail)) {
    return { status: "inconclusive", reason: "email lookup unavailable" };
  }

  if (byEmail.data.length > 0) {
    return { status: "configured" };
  }

  const usersResponse = await fetchWithRetry(() => getUsers(clientId, token));
  if (!hasUpstreamListPayload(usersResponse)) {
    return { status: "inconclusive", reason: "user list unavailable" };
  }

  if (usersResponse.data.length === 0) {
    return {
      status: "inconclusive",
      reason: "partner user list empty — cannot verify submitting user",
    };
  }

  if (findConfiguredUserInList(usersResponse.data, email)) {
    return { status: "configured" };
  }

  const substituteResult = await findSubstituteSubmittingUserFromList(
    usersResponse.data,
    clientId,
    token
  );

  if (substituteResult?.inconclusive) {
    return {
      status: "inconclusive",
      reason: substituteResult.reason ?? "substitute lookup inconclusive",
    };
  }

  if (substituteResult?.substitute) {
    return {
      status: "substituted",
      substitute: substituteResult.substitute,
      roleName: substituteResult.roleName,
    };
  }

  return { status: "not_found", confident: true };
}

export async function checkAndResolveSubmittingUserWithRetry(
  clientId,
  submittingUserEmail,
  token,
  { attempts = 3, delayMs = 2000 } = {}
) {
  let lastResult = { status: "inconclusive", reason: "not attempted" };

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    lastResult = await checkAndResolveSubmittingUser(
      clientId,
      submittingUserEmail,
      token
    );
    if (lastResult.status !== "inconclusive") {
      return lastResult;
    }
    if (attempt < attempts - 1) {
      await sleep(delayMs * (attempt + 1));
    }
  }

  return lastResult;
}

/** One Slack alert per shared app + configured/substitute email pair per page load. */
export async function notifySubmittingUserSubstitution(
  app,
  id,
  configuredEmail,
  substituteEmail,
  roleName
) {
  const notifyKey = `${id ?? ""}|${normalizeSubmittingUserEmail(configuredEmail).toLowerCase()}|${normalizeSubmittingUserEmail(substituteEmail).toLowerCase()}`;
  if (submittingUserSubstitutionNotifyKeys.has(notifyKey)) {
    return null;
  }
  submittingUserSubstitutionNotifyKeys.add(notifyKey);
  const notice = submittingUserSubstitutedMessage(
    configuredEmail,
    substituteEmail,
    roleName
  );
  return notifyAdminErrorMessage(app, id, notice, true);
}
